/* UserCredits.js */
/* @author: anghello */

import React, { Fragment, useCallback, useEffect, useState } from 'react';
import { Row, Col, Card, CardBody, Button, UncontrolledTooltip, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import { useAuth } from '../../../context/auth/AuthContext';
import Strings from '../../../translations/Translations';
import SubscriptionsStatuses from '../../../enums/SubscriptionsStatuses';
import { Subscriptions } from '../../../enums/Subscriptions';
import { FaCog, FaChevronDown, FaChevronUp, FaInfoCircle } from "react-icons/fa";
import AppleStoreImage from '../../../assets/img/apple_pp_store-267x89.png';
import PlayStoreImage from '../../../assets/img/google-play-badge-300x89.png';
import './usercredits.scss';
import ModalCreditsTransactions from './../ModalCreditsTransactions/ModalCreditsTransactions';
import ModalCreditsDetails from './../ModalCreditsDetails/ModalCreditsDetails';

const POPFANS_APP_IOS_LINK = process.env.REACT_APP_POPFANS_APP_IOS_LINK;
const POPFANS_APP_ANDROID_LINK = process.env.REACT_APP_POPFANS_APP_ANDROID_LINK;

const convertExpireDate = (date) => {
  const dateString = `${date}`;
  const year = dateString.substring(0, 4);
  const month = dateString.substring(4, 6);
  const day = dateString.substring(6, 8);
  return `${year}/${month}/${day}`;
};

export default function UserCredits() {
  const {
    toggleLoading,
    setOpenModalSubscribe,
    setOpenModalCancelSubscription,
    setShowMessageNoCredits,
    globalCreditsUserData,
    globalCreditsUserBalance,
    getUserDataCredits,
    getUserBalance
  } = useAuth();
  const [creditsDetails, setCreditsDetails] = useState(false);

  const [subscribeLabel, setSubscribeLabel] = useState(Strings('upgrade_subscription'));

  const [selectedSubscription, setSelectedSubscription] = useState();

  const [modalManageSubscription, setModalManageSubscription] = useState(false);
  const toggleModalManageSubscription = useCallback(() => {
    setModalManageSubscription(!modalManageSubscription);
  }, [modalManageSubscription]);

  const SubscriptionBadge = useCallback(({ subscription, index, status, type }) => {
    let backgroundClass = '', labelStatus = '', labelType = '', labelClient = '';
    let labelUntil = Strings('auto_refill');

    const subscriptionStatus = (status) ? parseInt(status) : parseInt(subscription.status);
    const subscriptionType = (type) ? parseInt(type) : parseInt(subscription.type);

    if (subscription) {
      if (subscription.status !== undefined) {
        if (subscriptionStatus === SubscriptionsStatuses.STATUS.NONE) {
          backgroundClass = 'text-bg-secondary';
        } else if (subscriptionStatus === SubscriptionsStatuses.STATUS.ACTIVE_FREE || subscriptionStatus === SubscriptionsStatuses.STATUS.ACTIVE_MONTHLY || subscriptionStatus === SubscriptionsStatuses.STATUS.ACTIVE_YEARLY) {
          backgroundClass = 'text-bg-success';
          labelStatus = Strings('active');
        } else if (subscriptionStatus === SubscriptionsStatuses.STATUS.PENDING) {
          backgroundClass = 'text-bg-warning';
          labelStatus = Strings('pending');
        } else if (subscriptionStatus === SubscriptionsStatuses.STATUS.EXPIRED) {
          backgroundClass = 'text-bg-danger';
          labelStatus = Strings('expired');
          labelUntil = Strings('until');
        } else if (subscriptionStatus === SubscriptionsStatuses.STATUS.CANCELED) {
          backgroundClass = 'text-bg-danger';
          labelStatus = Strings('canceled');
          labelUntil = Strings('until');
        }
      }

      if (subscription.type !== undefined) {
        if (subscriptionType === SubscriptionsStatuses.TYPE.FREE) {
          labelType = Strings('free');
        } else if (subscriptionType === SubscriptionsStatuses.TYPE.PLUS) {
          labelType = Subscriptions.plus.title;
        } else if (subscriptionType === SubscriptionsStatuses.TYPE.PREMIUM) {
          labelType = Subscriptions.premium.title;
        } else if (subscriptionType === SubscriptionsStatuses.TYPE.MAX) {
          labelType = Subscriptions.max.title;
        }
      }

      let client, clientTootlip = '';
      // if (subscription.client === 'HTML') {
      //   client = 'WEB';
      //   clientTootlip = Strings('subscription_managed_web');
      // }
      if (subscription.client === 'IOS') {
        client = 'APPLE';
        clientTootlip = Strings('subscription_managed_ios');
      }
      else if (subscription.client === 'AND') {
        client = 'GOOGLE';
        clientTootlip = Strings('subscription_managed_and');
      }
      if (subscription.client !== 'HTML') {
        labelClient = (
          <>
            <span className='badge bg-pz-1' id={`subscription-tooltip-${index}`}>{client}</span>
            <UncontrolledTooltip placement='left' target={`subscription-tooltip-${index}`}>{clientTootlip}</UncontrolledTooltip>
          </>
        );
        labelUntil = Strings('auto_refill');
      }
    }

    return (
      <Row className='align-items-center'>
        <Col sm={12} md={9}>
          <p className={`text-uppercase mb-0 font-weight-light text-white`}>
            <span className={`badge ${backgroundClass} border-radius-0`}>
              {Strings('subscription')} {labelStatus}
            </span>
          </p>
          <p className="h3 font-weight-bold mb-0">{labelType}</p>
        </Col>
        <Col sm={12} md={3}>
          <div className='d-flex justify-content-end gap-2'>
            <div>
              {parseInt(subscription.status) === SubscriptionsStatuses.STATUS.ACTIVE_MONTHLY && (
                <p className='mb-0 font-weight-light text-end'>
                  <small>{labelUntil}: {convertExpireDate(subscription.expire)}</small>
                </p>
              )}
              <div className='d-flex justify-content-end gap-2'>
                <p className='mb-0'>{labelClient}</p>
                {parseInt(subscription.status) === SubscriptionsStatuses.STATUS.ACTIVE_MONTHLY && (
                  <p className='mb-0'>
                    <span
                      className='cursor-pointer'
                      onClick={() => {
                        setSelectedSubscription(subscription);
                        toggleModalManageSubscription();
                      }}
                    >
                      <FaCog />
                    </span>
                  </p>
                )}
              </div>
            </div>
          </div>
        </Col>
      </Row>
    );
  }, [toggleModalManageSubscription]);

  const getUserData = useCallback(async () => {
    toggleLoading(true);

    await getUserDataCredits();
    await getUserBalance();

    toggleLoading(false);

    setShowMessageNoCredits(false);
  }, [getUserDataCredits, getUserBalance, setShowMessageNoCredits, toggleLoading]);

  useEffect(() => { getUserData(); }, [getUserData]);

  const getMaxSubscriptionType = useCallback((subscriptions) => {
    let maxSubscriptionType = 0;
    if (subscriptions && subscriptions.length) {
      subscriptions.forEach((item) => {
        if (parseInt(item.type) > maxSubscriptionType && parseInt(item.status) === SubscriptionsStatuses.STATUS.ACTIVE_MONTHLY) {
          maxSubscriptionType = parseInt(item.type);
        }
      });
    }
    return maxSubscriptionType;
  }, []);

  useEffect(() => {
    if (globalCreditsUserData && globalCreditsUserData.subscriptions && globalCreditsUserData.subscriptions.length) {
      const maxSubscriptionType = getMaxSubscriptionType(globalCreditsUserData.subscriptions);
      let labelToSet;

      if (maxSubscriptionType === 0) {
        labelToSet = Strings('subscribe_now');
      } else if (maxSubscriptionType === SubscriptionsStatuses.TYPE.MAX) {
        labelToSet = Strings('manage_subscription');
      }

      if (labelToSet) {
        setSubscribeLabel(labelToSet);
      }
    }
  }, [globalCreditsUserData, getMaxSubscriptionType]);

  const SubscriptionBadgeStatus = useCallback(({ subscription }) => {
    let backgroundClass = '', labelStatus = '';

    const subscriptionStatus = parseInt(subscription.status);

    if (subscriptionStatus === SubscriptionsStatuses.STATUS.NONE) {
      backgroundClass = 'text-bg-secondary';
    } else if (subscriptionStatus === SubscriptionsStatuses.STATUS.ACTIVE_FREE || subscriptionStatus === SubscriptionsStatuses.STATUS.ACTIVE_MONTHLY || subscriptionStatus === SubscriptionsStatuses.STATUS.ACTIVE_YEARLY) {
      backgroundClass = 'text-bg-success';
      labelStatus = Strings('active');
    } else if (subscriptionStatus === SubscriptionsStatuses.STATUS.PENDING) {
      backgroundClass = 'text-bg-warning';
      labelStatus = Strings('pending');
    } else if (subscriptionStatus === SubscriptionsStatuses.STATUS.EXPIRED) {
      backgroundClass = 'text-bg-danger';
      labelStatus = Strings('expired');
    } else if (subscriptionStatus === SubscriptionsStatuses.STATUS.CANCELED) {
      backgroundClass = 'text-bg-danger';
      labelStatus = Strings('canceled');
    }

    return (
      <p className={`text-uppercase mb-2 font-weight-light text-white`}>
        <span className={`badge ${backgroundClass}`}>
          {Strings('subscription')} {labelStatus}
        </span>
      </p>
    );
  }, []);

  const getSubscriptionTypeLabel = useCallback((subscription) => {
    const subscriptionType = parseInt(subscription.type);
    let labelType = '';
    if (subscriptionType === SubscriptionsStatuses.TYPE.FREE) {
      labelType = Strings('free');
    } else if (subscriptionType === SubscriptionsStatuses.TYPE.PLUS) {
      labelType = Subscriptions.plus.title;
    } else if (subscriptionType === SubscriptionsStatuses.TYPE.PREMIUM) {
      labelType = Subscriptions.premium.title;
    } else if (subscriptionType === SubscriptionsStatuses.TYPE.MAX) {
      labelType = Subscriptions.max.title;
    }
    return labelType;
  }, []);

  const getSubscriptionClientLabel = useCallback((subscription) => {
    let client;
    if (subscription.client === 'HTML') {
      client = 'WEB';
    }
    else if (subscription.client === 'IOS') {
      client = 'APPLE';
    }
    else if (subscription.client === 'AND') {
      client = 'GOOGLE';
    }
    return client;
  }, []);

  const [isOpenModalTransactions, setIsOpenModalTransactions] = useState(false);
  const [isOpenModalDetails, setIsOpenModalDetails] = useState(false);

  const getMainSubscriptionBadge = useCallback(() => {
    if (globalCreditsUserData && globalCreditsUserData.subscriptions && globalCreditsUserData.subscriptions[0]) {
      const status = parseInt(globalCreditsUserData.subscriptions[0].status);
      if (status !== SubscriptionsStatuses.STATUS.CANCELED && status !== SubscriptionsStatuses.STATUS.EXPIRED && status !== SubscriptionsStatuses.STATUS.PENDING) {
        return <SubscriptionBadge subscription={globalCreditsUserData.subscriptions[0]} />
      } else {
        return <SubscriptionBadge subscription={globalCreditsUserData.subscriptions[0]} status={SubscriptionsStatuses.STATUS.ACTIVE_FREE} type={SubscriptionsStatuses.TYPE.FREE} />
      }
    }
  }, [globalCreditsUserData]);

  return (
    <Fragment>

      <Card className='mb-4 border-radius-4px'>
        <CardBody>
          <Row className='mb-3'>
            <Col>
              <h2 className='h6 fw-bold'>
                {Strings('my_plan')}
              </h2>
            </Col>
          </Row>

          <Row className='mb-2'>
            <Col sm={12}>
              {getMainSubscriptionBadge()}
            </Col>
          </Row>
          <Row>
            <Col className='action-button-row-1'>
              <Button
                color='primary'
                className='pb-btn-3 shadow'
                onClick={() => setOpenModalSubscribe(true)}
              >
                {subscribeLabel}
              </Button>
            </Col>
          </Row>

          {globalCreditsUserData && globalCreditsUserData.subscriptions.length > 1 && (
            <Row>
              {globalCreditsUserData && globalCreditsUserData.subscriptions && globalCreditsUserData.subscriptions[1] && (
                <Col sm={6}>
                  <SubscriptionBadge subscription={globalCreditsUserData.subscriptions[1]} index={1} />
                </Col>
              )}
              {globalCreditsUserData && globalCreditsUserData.subscriptions && globalCreditsUserData.subscriptions[2] && (
                <Col sm={6}>
                  <SubscriptionBadge subscription={globalCreditsUserData.subscriptions[2]} index={2} />
                </Col>
              )}
            </Row>
          )}
        </CardBody>
      </Card>

      <Card className='mb-4 border-radius-4px'>
        <CardBody>
          <Row className='mb-3'>
            <Col>
              <h2 className='h6 fw-bold'>
                {Strings('credits')}
              </h2>
            </Col>
          </Row>
          <Row className="mb-3">
            <Col sm={12} md={6} className='d-flex align-items-center gap-3'>
              <div>
                <span className="h2 font-weight-bold mb-0">{globalCreditsUserBalance && globalCreditsUserBalance.credits_total}</span>
                <h6 className="card-title text-muted mb-0">{Strings('credit_balance')}</h6>
              </div>
              {globalCreditsUserBalance && !!globalCreditsUserBalance.credits_total && (
                <span className='cursor-pointer' onClick={() => setCreditsDetails(value => !value)}>
                  {!creditsDetails && <FaChevronDown size={20} />}
                  {creditsDetails && <FaChevronUp size={20} />}
                </span>
              )}
            </Col>
            <Col sm={12} md={6} className='action-button-row-1'>
              <Button
                color='primary'
                className='pb-btn-3 shadow'
                onClick={() => setOpenModalSubscribe(true)}
              >
                {Strings('get_credits')}
              </Button>
            </Col>
          </Row>
          {creditsDetails && (
            <Row>
              {globalCreditsUserBalance && !!globalCreditsUserBalance.credits_monthly && (
                <Col sm={12} md={4}>
                  <Card>
                    <CardBody>
                      <Row>
                        <Col>
                          <h6 className="card-title text-muted mb-0">
                            {Strings('monthly')} <FaInfoCircle size={14} id='tooltip-credits-monthly' />
                          </h6>
                          <UncontrolledTooltip placement='right' target='tooltip-credits-monthly'>{Strings('tooltip_credits_monthly')}</UncontrolledTooltip>
                          <span className="h2 font-weight-bold mb-0">{globalCreditsUserBalance && globalCreditsUserBalance.credits_monthly}</span>
                        </Col>
                      </Row>
                    </CardBody>
                  </Card>
                </Col>
              )}
              {globalCreditsUserBalance && !!globalCreditsUserBalance.credits_yearly && (
                <Col sm={12} md={4}>
                  <Card>
                    <CardBody>
                      <Row>
                        <Col>
                          <h6 className="card-title text-muted mb-0">
                            {Strings('packs')} <FaInfoCircle size={14} id='tooltip-credits-packs' />
                          </h6>
                          <UncontrolledTooltip placement='right' target='tooltip-credits-packs'>{Strings('tooltip_credits_packs')}</UncontrolledTooltip>
                          <span className="h2 font-weight-bold mb-0">{globalCreditsUserBalance && globalCreditsUserBalance.credits_yearly}</span>
                        </Col>
                      </Row>
                    </CardBody>
                  </Card>
                </Col>
              )}
              {globalCreditsUserBalance && !!globalCreditsUserBalance.credits_bonus && (
                <Col sm={12} md={4}>
                  <Card>
                    <CardBody>
                      <Row>
                        <Col>
                          <h6 className="card-title text-muted mb-0">
                            {Strings('bonus')} <FaInfoCircle size={14} id='tooltip-credits-bonus' />
                          </h6>
                          <UncontrolledTooltip placement='right' target='tooltip-credits-bonus'>{Strings('tooltip_credits_bonus')}</UncontrolledTooltip>
                          <span className="h2 font-weight-bold mb-0">{globalCreditsUserBalance && globalCreditsUserBalance.credits_bonus}</span>
                        </Col>
                      </Row>
                    </CardBody>
                  </Card>
                </Col>
              )}
            </Row>
          )}
          <Row>
            <Col className='d-flex gap-2'>
              <p className='text-primary underline p cursor-pointer m-0' onClick={() => setIsOpenModalTransactions(true)}>
                {Strings('transactions')}
              </p>
              <p className='text-primary underline p cursor-pointer m-0' onClick={() => setIsOpenModalDetails(true)}>
                {Strings('details')}
              </p>
            </Col>
          </Row>
        </CardBody>
      </Card>

      <ModalCreditsTransactions isOpen={isOpenModalTransactions} setIsOpen={setIsOpenModalTransactions} />

      <ModalCreditsDetails isOpen={isOpenModalDetails} setIsOpen={setIsOpenModalDetails} />

      {/* Modal Manage Subscription */}
      <Modal isOpen={modalManageSubscription} toggle={toggleModalManageSubscription} size='md'>
        <ModalHeader toggle={toggleModalManageSubscription}>
          {Strings('manage_subscription')}
        </ModalHeader>
        <ModalBody>
          {selectedSubscription && (
            <>
              <Row>
                <Col>
                  <Card className={`shadow`}>
                    <CardBody>
                      <Row className='align-items-center'>
                        <Col>
                          <SubscriptionBadgeStatus subscription={selectedSubscription} />
                          <p className="h2 font-weight-bold mb-0">{getSubscriptionTypeLabel(selectedSubscription)}</p>
                          <p className='mb-0 font-weight-light'>
                            {selectedSubscription.client === 'HTML' && `${Strings('auto_refill')}:`}
                            {selectedSubscription.client !== 'HTML' && `${Strings('until')}:`}
                            {' '}
                            {convertExpireDate(selectedSubscription.expire)}
                          </p>
                          <p className='mb-0'><span className='badge bg-pz-1'>{getSubscriptionClientLabel(selectedSubscription)}</span></p>
                        </Col>
                      </Row>
                    </CardBody>
                  </Card>
                </Col>
              </Row>

              {selectedSubscription.client === 'HTML' && selectedSubscription.type !== SubscriptionsStatuses.TYPE.FREE && (
                <Row className='mt-4'>
                  <Col md={12}>
                    <h6>{Strings('options')}</h6>
                  </Col>
                  {/* <Col md={12} className='mb-2'>
                    <Card className='shadow'>
                      <CardBody>
                        <p className='mb-1 h6'>{Strings('payment_method')}</p>
                        <p className='mb-1'>{Strings('payment_method_text')}</p>
                        <div className='d-flex justify-content-end'>
                          <Button color='secondary'>{Strings('change_payment_method')}</Button>
                        </div>
                      </CardBody>
                    </Card>
                  </Col> */}
                  <Col md={12}>
                    <Card className='shadow'>
                      <CardBody>
                        <p className='mb-1 h6'>{Strings('cancel_subscription')}</p>
                        <p className='mb-1'>{Strings('cancel_subscription_text')}</p>
                        <div className='d-flex justify-content-end'>
                          <Button color='danger' onClick={() => setOpenModalCancelSubscription(true)}>
                            {Strings('cancel_subscription')}
                          </Button>
                        </div>
                      </CardBody>
                    </Card>
                  </Col>
                </Row>
              )}

              {selectedSubscription.client !== 'HTML' && (
                <Row className='mt-4'>
                  <Col>
                    <Card>
                      <CardBody>
                        {selectedSubscription.client === 'IOS' && (
                          <>
                            <p className='mb-2'>{Strings('subscription_managed_ios')}</p>
                            <p className='mb-0'>
                              <a href={POPFANS_APP_IOS_LINK} target="_blank" rel="noopener noreferrer">
                                <img src={AppleStoreImage} className="mr-2 get_app_image_button" alt={Strings('download_popfans_app_for_ios')} />
                              </a>
                            </p>
                          </>
                        )}
                        {selectedSubscription.client === 'AND' && (
                          <>
                            <p className='mb-2'>{Strings('subscription_managed_and')}</p>
                            <p className='mb-0'>
                              <a href={POPFANS_APP_ANDROID_LINK} target="_blank" rel="noopener noreferrer">
                                <img src={PlayStoreImage} className="mr-2 get_app_image_button" alt={Strings('download_popfans_app_for_android')} />
                              </a>
                            </p>
                          </>
                        )}
                      </CardBody>
                    </Card>
                  </Col>
                </Row>
              )}
            </>
          )}
        </ModalBody>
        <ModalFooter className='d-flex justify-content-start'>
          <Button color='secondary' onClick={toggleModalManageSubscription}>
            {Strings('close')}
          </Button>
        </ModalFooter>
      </Modal>

    </Fragment >
  );
}